import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

// Chart colours chosen from the legend, saved per widget in the shared dashboard layout: colors[widgetId][seriesKey] = '#hex'.
const Ctx = createContext({ colors: {}, set: null });
export const WidgetIdCtx = createContext('');

export function DashColorsProvider({ colors, onSave, children }) {
  const [local, setLocal] = useState(colors || {});
  const t = useRef(null);
  const pending = useRef({});
  useEffect(() => { setLocal(colors || {}); }, [colors]);
  // The picker fires while dragging: show at once, save once it settles.
  const set = onSave ? (wid, key, hex) => {
    setLocal((c) => ({ ...c, [wid]: { ...(c[wid] || {}), [key]: hex } }));
    pending.current = { ...pending.current, [wid]: { ...(pending.current[wid] || {}), [key]: hex } };
    clearTimeout(t.current);
    t.current = setTimeout(() => { const batch = pending.current; pending.current = {}; onSave(batch); }, 500);
  } : null;
  return <Ctx.Provider value={{ colors: local, set }}>{children}</Ctx.Provider>;
}

// pick(key, fallback) → colour for this widget's series; edit(key, hex) when the user may change it.
export function useWidgetColors() {
  const { colors, set } = useContext(Ctx);
  const wid = useContext(WidgetIdCtx);
  return { pick: (key, fb) => colors?.[wid]?.[key] || fb, edit: set && wid ? (key, hex) => set(wid, key, hex) : null, wid };
}

const toHex = (c) => (/^#[0-9a-f]{6}$/i.test(c) ? c : '#0f6b4f');

// Legend dot; click it to choose a new colour (invisible native colour input on top).
export function Swatch({ color, onPick, name, size = 'h-2.5 w-2.5', testid }) {
  if (!onPick) return <span className={`${size} shrink-0 rounded-full`} style={{ background: color }} />;
  return (
    <label className={`relative ${size} shrink-0 cursor-pointer rounded-full ring-offset-1 ring-offset-card transition-shadow hover:ring-2 hover:ring-foreground/30`} style={{ background: color }} title={`Change colour of ${name}`} data-testid={testid}>
      <input type="color" value={toHex(color)} onChange={(e) => onPick(e.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label={`Colour of ${name}`} />
    </label>
  );
}

// Chart legend with clickable colour dots. items: [{ key, name, color }].
export function ChartLegend({ items }) {
  const { edit, wid } = useWidgetColors();
  if (!items.length) return null;
  return (
    <ul className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground" data-testid="tagws-chart-legend">
      {items.map((it) => (
        <li key={it.key} className="flex items-center gap-1.5">
          <Swatch color={it.color} name={it.name} onPick={edit ? (hex) => edit(it.key, hex) : null} testid={`tagws-legend-color-${wid}-${String(it.key).replace(/\s+/g, '_')}`} />
          <span className="text-foreground/80">{it.name}</span>
        </li>
      ))}
    </ul>
  );
}
